// Copyright (c) Ping. All rights reserved.
//
// Mounts the redesigned shared (RN + NativeWind) Settings modal in
// the web shell's `settings` modal slot. Mirrors the desktop host
// (`apps/desktop/src/shell/hosts/SettingsModalHost.tsx`) one-for-one;
// the difference is the BE-client modules and the avatar picker
// (web has a real `<input type=file>` — see Option A in the migration
// note for why this lives at the host level and not inside the
// shared pane).
//
// BE touch points:
//   - PATCH /v1/me/profile                          (display name —
//                                                   canonical, shared
//                                                   with signup flow)
//   - POST  /v1/profiles/avatar/upload-url          (avatar presign)
//   - PUT   /v1/profiles                            (avatar bind —
//                                                   legacy, retire
//                                                   once /v1/media
//                                                   ships)
//   - POST  /v1/auth/logout?all_devices=true        (sign out everywhere)
//   - GET   /v1/users/{user_id}/devices             (encryption pane)

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MessagingClient } from 'ping-openmls-sdk';

import {
  type AppearanceBackground,
  type AppearanceTheme,
  encryptRecoveryBlob,
  getLinkingTicket,
  type LinkingAcceptInput,
  type LinkingBuildInput,
  type LinkingBuildResult,
  postLinkingTicket,
  postRecoveryBlob,
  pubkeyHashB64Url,
  type RecoveryBackupStatus,
  type RecoveryUploadInput,
  revokeDevice,
  SettingsModal,
} from '@ping/ui';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';
import { identityManager } from '@ping/ui/src/platform/identity.web';

import {
  osNotificationPermission,
  osNotificationsGranted,
  requestOsNotificationPermission,
} from '@app/notifications/webNotifications';
import { log } from '@core/log/log';
import { api } from '@core/network';
import {
  bytesToBase64Url,
  getUserIdentityPubkeyBytes,
  signDeviceBinding,
} from '@core/network/deviceKeys';
import {
  claimAdmissionKeyPackages,
  fetchPendingAdmissions,
  getUserProfile,
} from '@core/network/users';
import { db } from '@core/persistence/db';
import { retrieveIdentity, storeIdentity } from '@core/persistence/identity';
import {
  clearMediaCache,
  computeMediaCacheUsage,
  type MediaCacheUsage,
} from '@core/persistence/mediaCacheStorage';
import { retrieveRefreshToken } from '@core/persistence/tokens';
import { preferencesStore } from '@core/preferences/PreferencesStore';
import { unregisterWebPush } from '@core/push/webPushRegistration';
import {
  readRecoveryBackupStatus,
  RECOVERY_BACKUP_KV_KEY,
} from '@features/auth/RecoveryBackupSetupHost';
import { buildRecoveryBlobV3 } from '@features/auth/recoveryBackupV3';
import {
  confirmRecoveryMnemonicSaved,
  retrieveRecoveryMnemonic,
} from '@features/auth/recoveryMnemonicStore';
import { getKycStatus, type KycStatus, startKyc } from '@features/business-directory';
import { fetchMediaDownloadUrl } from '@features/chat/mediaUpload';
import { resetDirectProfileIntroSession } from '@features/chat/resendDirectProfileIntro';
import { sendLinkingHistoryToDevice } from '@features/history/sendLinkingHistory';
import {
  clearPendingOutgoingLink,
  getPendingOutgoingLink,
  mergePendingOutgoingLinkAdmitted,
  savePendingOutgoingLink,
} from '@features/linking/pendingOutgoingLink';
import { QrRenderer } from '@features/linking/QrRenderer';
import { scanQrCode } from '@features/linking/scanQrCode';
import {
  TOUR_HOME,
  TOUR_SETTINGS,
  usePaneGuideStore,
  useTourReplay,
} from '@features/onboarding/tour';
import { useSelfProfile } from '@features/profile/selfProfileStore';
import { usePreferences } from '@features/settings/usePreferences';
import { PersonaKycModal } from '@features/settings/web/PersonaKycModal';
import {
  type AvatarContentType,
  getProfile,
  legacyUpdateProfileAvatar,
  type ProfileResponse,
  requestAvatarUploadUrl,
  updateProfile,
  uploadAvatarBytes,
} from '@features/settings/web/profilesApi';
import {
  listSessions,
  logoutAllDevices,
  logoutCurrentDevice,
  type SessionDevice,
} from '@features/settings/web/sessionsApi';
import { sendSelfProfileChanged } from '@features/tasks/deviceGroupTaskSync';
import { showCleaningOverlay } from '@session/cleaningOverlay';
import { clearAllLocalSessionState } from '@session/clearLocalState';
import { forceCleanReload } from '@session/forceCleanReload';
import { hardWipeBrowserAuthStorage } from '@session/hardWipeBrowserAuthStorage';
import { getSdkDeviceIdBytes } from '@session/sdkDeviceId';
import { getCurrentTransport } from '@session/sessionBootstrap';
import { useRouter } from '@shell/RouterContext';

interface LoadedIdentity {
  readonly displayName: string;
  readonly deviceId: string;
  readonly accountId: string;
}

function base64UrlToBytes(s: string): Uint8Array {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/');
  const fill = (4 - (padded.length % 4)) % 4;
  const bin = atob(padded + '='.repeat(fill));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToUrlBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Lowercase hex — matches the local conversation-row primary key
// (`useIncomingConversations` / `ensureConversation` both key rows on
// `bytesToHex(conv.id)`).
function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += bytes[i]!.toString(16).padStart(2, '0');
  return out;
}

// Map Persona's embedded inquiry status (from the <Inquiry> onComplete
// callback) to our coarse KycStatus, so the Settings row reflects the
// outcome the user just experienced IMMEDIATELY — without waiting on the
// server webhook (which can't reach a localhost dev box at all). The
// canonical server status is reconciled on the next Settings open.
function mapPersonaInquiryStatus(personaStatus: string): KycStatus {
  switch (personaStatus.toLowerCase()) {
    case 'approved':
      return 'approved';
    case 'declined':
    case 'failed':
    case 'expired':
      return 'declined';
    // 'completed' = submitted, awaiting a decision → show "under review".
    case 'completed':
    case 'needs_review':
      return 'needs_review';
    default:
      return 'pending';
  }
}

interface DeviceItemWire {
  device_id: string;
  signature_public_key_ref: string;
}

interface DevicesWireResponse {
  devices: readonly DeviceItemWire[];
}

// Polling backoff for the post-link chat reconciler. Keep aggressive
// at the start (the new device's bootstrap typically finishes within
// 2-5 s) and back off for the long tail (deploys / suspended tabs).
const RECONCILE_BACKOFFS_MS = [1000, 1500, 2000, 3000, 5000, 8000];
const RECONCILE_BUDGET_MS = 2 * 60 * 1000; // 2 minutes — well past the LinkingTicket TTL

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Background reconciler that runs on the EXISTING device after a
 * linking ticket is posted. Walks every chat group the existing
 * device is in (excluding the device group, which already saw an
 * `addMembers` inside `build_linking_ticket`) and admits the new
 * device into each one via the standard MLS `addMembers` flow.
 *
 * Why this is needed: `LinkingTicket.catchup_snapshot` only carries
 * MLS STATE for the new device to import locally — the BE's
 * `auth.conversation_members` table is never updated, so every
 * `fetchSince` from the new device on those chats 404s and gets
 * orphan-pruned. Each `addMembers` call here sends a Commit to the
 * BE (updates membership) AND a Welcome routed to the new device's
 * KP (the new device's WS picks it up on subscribe and the SDK
 * auto-joins via the `useIncomingConversations` hook).
 *
 * Each `addMembers` consumes one fresh KP from the new device's
 * pool. We claim one KP per chat via `/v1/devices/{accountId}`;
 * the new device's bootstrap calls `replenishKeyPackages(10)`, so
 * up to 10 chats transfer in a single link round.
 *
 * Lifecycle: fire-and-forget. Module-level — the function reads the
 * SDK client from `useSessionStore.getState()` and uses module-level
 * helpers, so it survives modal close / re-render / unmount.
 */
async function reconcileChatMembershipForLinkedDevice(input: {
  accountId: string;
  newDeviceIdBase64Url: string;
  // [#2] New device's ephemeral X25519 pubkey (from the QR) + this device's id,
  // so after admit we can ship decrypted history sealed to that key. Optional so
  // any legacy caller still type-checks; history send is skipped when absent.
  newDevicePubBase64Url?: string;
  selfDeviceIdBase64Url?: string;
  // Conversations the new device was already admitted to on a prior
  // (interrupted) pass — skipped here so we never re-add an existing member
  // (which would create a duplicate MLS leaf). Drives durable resume.
  excludeConvHex?: Set<string>;
}): Promise<void> {
  // NOTE: `accountId` stays in the input type for callers, but the reconciler
  // no longer needs it — KeyPackages are now claimed by the target device id via
  // the consuming admission-claim endpoint, not by account via the LIST.
  const { newDeviceIdBase64Url, newDevicePubBase64Url, selfDeviceIdBase64Url } = input;
  const excludeConvHex = input.excludeConvHex ?? new Set<string>();
  log.info({
    event: 'settings.linking.reconcile.start',
    deviceIdPrefix: newDeviceIdBase64Url.slice(0, 8),
  });

  // 1) Wait for the new device to appear in /v1/auth/sessions —
  //    confirms it called `link-verify` successfully. If the timeout
  //    hits we bail; the user can always close + reopen Settings to
  //    re-trigger via a fresh build.
  const deadline = Date.now() + RECONCILE_BUDGET_MS;
  let attempt = 0;
  let detected = false;
  while (Date.now() < deadline) {
    try {
      const sessions = await listSessions();
      if (sessions.some((s) => s.deviceId === newDeviceIdBase64Url)) {
        detected = true;
        break;
      }
    } catch {
      /* transient — retry */
    }
    await sleep(RECONCILE_BACKOFFS_MS[Math.min(attempt, RECONCILE_BACKOFFS_MS.length - 1)] ?? 8000);
    attempt += 1;
  }
  if (!detected) {
    log.warn({ event: 'settings.linking.reconcile.timeout_sessions' });
    return;
  }

  // 2) Pull the existing chats from the SDK. Filter device groups
  //    (handled by `build_linking_ticket` and not addressable via
  //    `addMembers` against the new device anyway).
  const session = useSessionStore.getState();
  if (session.status.kind !== 'authenticated') {
    log.warn({ event: 'settings.linking.reconcile.no_client' });
    return;
  }
  // The SDK's `admitDeviceToChats` (added in ping-openmls-sdk 0.4.2) is
  // the canonical post-link reconciliation entry point. It accepts one
  // KP per chat, performs `add_members` + sends Commit + Welcome (with
  // automatic `?recipient=` priming via the new `setNextWelcomeRecipients`
  // transport hook), and returns per-chat outcomes — so a single bad KP
  // doesn't strand the user on the other chats.
  const sdkClient = session.status.client as unknown as {
    listConversations: () => Promise<Array<{ id: Uint8Array; is_device_group: boolean }>>;
    syncConversations?: () => Promise<unknown>;
    admitDeviceToChats: (
      deviceId: Uint8Array,
      entries: Array<{ conversationId: Uint8Array; keyPackage: Uint8Array }>,
    ) => Promise<
      Array<{
        conversationId: Uint8Array;
        status: 'admitted' | 'skipped' | 'failed';
        reason?: string;
        error?: string;
      }>
    >;
    send?: (conv: Uint8Array, plaintext: Uint8Array, nowMs?: number) => Promise<unknown>;
  };
  // Admit against a COMPLETE roster: `listConversations` reads the SDK's
  // in-memory set, which can be partial if reconcile runs before the initial
  // sync finished or a prior `syncConversations` errored. Admitting a partial
  // set silently strands the new device on the missing chats. Force a fresh sync
  // (retried) first so the admit set is complete — the "linked device missing
  // conversations" fix. Best-effort: a sync failure falls back to whatever the
  // SDK already has (the durable resume retries the remainder later).
  if (typeof sdkClient.syncConversations === 'function') {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        await sdkClient.syncConversations();
        break;
      } catch {
        if (attempt < 2) await new Promise((r) => setTimeout(r, 1000));
      }
    }
  }
  let allConvs: Array<{ id: Uint8Array; is_device_group: boolean }>;
  try {
    allConvs = await sdkClient.listConversations();
  } catch (err) {
    log.warn({
      event: 'settings.linking.reconcile.list_failed',
      message: (err as Error).message,
    });
    return;
  }
  // Exclude conversations the user has soft-deleted LOCALLY. `delete chat` is a
  // local soft-delete (`deletedAt > 0`) that leaves the MLS group intact, so
  // `listConversations()` still returns it — but admitting the new device to a
  // chat the user deleted would resurrect that deleted conversation on the
  // freshly-linked device. Skip them so a linked device mirrors what the user
  // actually sees.
  let deletedConvHex = new Set<string>();
  try {
    const deletedRows = await db.conversations.filter((c) => (c.deletedAt ?? 0) > 0).toArray();
    deletedConvHex = new Set(deletedRows.map((c) => c.id.toLowerCase()));
  } catch (err) {
    log.warn({
      event: 'settings.linking.reconcile.deleted_lookup_failed',
      message: (err as Error).message,
    });
  }
  const chats = allConvs.filter((c) => {
    const hex = bytesToHex(c.id);
    return !c.is_device_group && !excludeConvHex.has(hex) && !deletedConvHex.has(hex.toLowerCase());
  });
  if (chats.length === 0) {
    log.info({
      event: 'settings.linking.reconcile.no_chats',
      msg: 'no non-DG conversations to transfer',
    });
    // Nothing left to admit (all already covered) → the link is complete.
    void clearPendingOutgoingLink();
    return;
  }

  // 3) Atomically CLAIM one distinct KeyPackage per chat for the new device.
  //    This CONSUMES single-use KeyPackages server-side (unlike the legacy
  //    non-consuming `GET /v1/devices/{accountId}` LIST, where two siblings
  //    draining the admission ledger concurrently would claim the SAME KP and
  //    the new device would silently drop the second Welcome). When the
  //    single-use pool can't cover every chat, the BE pads with the device's
  //    reusable last-resort KeyPackage, so a large account no longer strands
  //    the surplus chats.
  //
  //    We accumulate across retries because the new device uploads its
  //    KeyPackage pool asynchronously AFTER it becomes admissible — an early
  //    claim can come back short (ordering), so we top up until we have one per
  //    chat or the deadline. Each claim requests only the shortfall, so we never
  //    over-consume.
  const newDeviceIdBytes = base64UrlToBytes(newDeviceIdBase64Url);
  const kps: Uint8Array[] = [];
  const claimDeadline = Date.now() + 30_000;
  let claimAttempt = 0;
  while (kps.length < chats.length && Date.now() < claimDeadline) {
    try {
      const batch = await claimAdmissionKeyPackages({
        deviceIdBase64Url: newDeviceIdBase64Url,
        count: chats.length - kps.length,
      });
      if (batch.length > 0) {
        kps.push(...batch);
        if (kps.length >= chats.length) {
          break;
        }
        log.info({
          event: 'settings.linking.reconcile.kp_pool_partial',
          claimed: kps.length,
          needed: chats.length,
        });
      }
    } catch {
      /* retry on transient errors */
    }
    await sleep(
      RECONCILE_BACKOFFS_MS[Math.min(claimAttempt, RECONCILE_BACKOFFS_MS.length - 1)] ?? 8000,
    );
    claimAttempt += 1;
  }

  const entries: Array<{ conversationId: Uint8Array; keyPackage: Uint8Array }> = [];
  let kpSkipped = 0;
  for (let i = 0; i < chats.length; i++) {
    const kp = kps[i];
    if (kp === undefined) {
      log.warn({
        event: 'settings.linking.reconcile.kp_unavailable',
        chatIdPrefix: bytesToUrlBase64(chats[i]!.id).slice(0, 8),
      });
      kpSkipped += 1;
      continue;
    }
    entries.push({ conversationId: chats[i]!.id, keyPackage: kp });
  }

  if (entries.length === 0) {
    log.info({
      event: 'settings.linking.reconcile.done',
      added: 0,
      skipped: kpSkipped,
      failed: 0,
      total: chats.length,
    });
    return;
  }

  // 4) Single SDK call — the worker dispatches each per-chat add +
  //    Commit/Welcome send + snapshot in order, with the BE-side
  //    membership row materialising as a side effect of each Welcome.
  let outcomes: Array<{
    conversationId: Uint8Array;
    status: 'admitted' | 'skipped' | 'failed';
    reason?: string;
    error?: string;
  }> = [];
  try {
    outcomes = await sdkClient.admitDeviceToChats(newDeviceIdBytes, entries);
  } catch (err) {
    log.warn({
      event: 'settings.linking.reconcile.admit_failed',
      message: (err as Error).message,
    });
    return;
  }

  let added = 0;
  let skipped = kpSkipped;
  let failed = 0;
  for (const outcome of outcomes) {
    const chatIdPrefix = bytesToUrlBase64(outcome.conversationId).slice(0, 8);
    if (outcome.status === 'admitted') {
      added += 1;
      log.info({ event: 'settings.linking.reconcile.added', chatIdPrefix });
    } else if (outcome.status === 'skipped') {
      skipped += 1;
      log.info({
        event: 'settings.linking.reconcile.skipped',
        chatIdPrefix,
        reason: outcome.reason,
      });
    } else {
      failed += 1;
      log.warn({
        event: 'settings.linking.reconcile.add_failed',
        chatIdPrefix,
        message: outcome.error,
      });
    }
  }
  log.info({
    event: 'settings.linking.reconcile.done',
    added,
    skipped,
    failed,
    total: chats.length,
  });

  // Durable progress: record the conversations we admitted this pass, and clear
  // the pending record entirely when nothing was skipped/failed (the new device
  // is now a member everywhere it needed to be). A pass that skipped/failed any
  // chat (KP shortage / transient admit failure) leaves the record so the next
  // app load resumes the remainder.
  const admittedHexThisPass = outcomes
    .filter((o) => o.status === 'admitted')
    .map((o) => bytesToHex(o.conversationId));
  void mergePendingOutgoingLinkAdmitted(admittedHexThisPass);
  if (skipped === 0 && failed === 0) {
    void clearPendingOutgoingLink();
  }

  // Re-send the PEER identity into each admitted DIRECT chat so the
  // freshly-linked device can name the 1:1 after the other party.
  // MLS `join` clears the conversation's embedded name and the
  // historical creation-time profile-intro is never replayed to a late
  // joiner, so without this the new device persists the chat as
  // "New chat" (`useIncomingConversations`). We carry `direct: true`
  // and the PEER's identity (read from this device's local row): the
  // new same-account device sets `name` to the peer, while the actual
  // peer ignores it (the ingest skips a profile about one's own
  // account — see `useIncomingMessages` ping.profile handler). Group
  // chats already hydrate via `com.ping.group_name`, so they're skipped.
  if (typeof sdkClient.send === 'function') {
    for (const outcome of outcomes) {
      // Re-send identity hydration for newly-admitted chats AND for chats where
      // admit "failed" because the device is ALREADY a member ("Duplicate
      // signature key in proposals and group") — in that state the linked device
      // is in the group but never received the title/avatar, so it would render
      // a placeholder / member-names instead of the real group name. Only a
      // genuine `skipped` (KeyPackage shortage → device is NOT a member yet) is
      // skipped, since a non-member can't receive the hydration anyway.
      if (outcome.status === 'skipped') continue;
      const convHex = bytesToHex(outcome.conversationId);
      void (async () => {
        try {
          const row = await db.conversations.get(convHex);
          if (row === undefined) return;
          // GROUP: re-send the group metadata (title + roster + avatar) so the
          // freshly-linked device gets the name + members — the creation-time
          // `com.ping.group_name`/`com.ping.group_avatar` were broadcast BEFORE
          // it joined, so without this the group stays an unnamed, member-less
          // "Group chat". Member display names/avatars also resolve from the
          // directory via `memberAccountIds`.
          if (row.kind === 'group') {
            const payload: Record<string, unknown> = {};
            if (row.name !== undefined && row.name.length > 0 && row.name !== 'Group chat') {
              payload.name = row.name;
            }
            if (row.memberAccountIds !== undefined && row.memberAccountIds.length > 0) {
              payload.memberAccountIds = row.memberAccountIds;
            }
            if (
              row.memberDisplayNames !== undefined &&
              Object.keys(row.memberDisplayNames).length > 0
            ) {
              payload.memberDisplayNames = row.memberDisplayNames;
            }
            if (Object.keys(payload).length > 0) {
              await sdkClient.send!(
                outcome.conversationId,
                new TextEncoder().encode(JSON.stringify({ kind: 'com.ping.group_name', payload })),
              ).catch(() => undefined);
            }
            if (row.avatarUri !== undefined && row.avatarUri.length > 0) {
              await sdkClient.send!(
                outcome.conversationId,
                new TextEncoder().encode(
                  JSON.stringify({
                    kind: 'com.ping.group_avatar',
                    payload: { blob_ref: { url: row.avatarUri } },
                  }),
                ),
              ).catch(() => undefined);
            }
            return;
          }
          // 1:1: send for any admitted chat with a resolvable peer account id;
          // a placeholder name is backfilled from the directory profile.
          if (row.peerAccountId === undefined || row.peerAccountId.length === 0) {
            return;
          }
          const isPlaceholder =
            row.name === undefined || row.name.length === 0 || row.name === 'New chat';
          let displayName = isPlaceholder ? undefined : row.name;
          let avatarMediaId: string | undefined;
          // Resolve the peer's directory display name + avatar. Always fetch the
          // avatar (the local row doesn't store a peer avatar media id); fall
          // back to the local name when the directory name is missing.
          const peer = await getUserProfile(row.peerAccountId).catch(() => null);
          if (peer !== null) {
            if (peer.displayName.length > 0) displayName = peer.displayName;
            if (peer.avatarMediaId !== undefined && peer.avatarMediaId.length > 0) {
              avatarMediaId = peer.avatarMediaId;
            }
          }
          // Still no name (directory miss + placeholder row) → nothing useful to
          // name the chat with; skip so we don't ship an empty displayName.
          if (displayName === undefined || displayName.length === 0) return;
          const profile = {
            kind: 'ping.profile',
            payload: {
              accountId: row.peerAccountId,
              displayName,
              ...(avatarMediaId !== undefined ? { avatarMediaId } : {}),
              direct: true,
            },
          };
          await sdkClient.send!(
            outcome.conversationId,
            new TextEncoder().encode(JSON.stringify(profile)),
          );
          log.info({
            event: 'settings.linking.peerProfile.sent',
            chatIdPrefix: convHex.slice(0, 8),
            hasAvatar: avatarMediaId !== undefined,
          });
        } catch (err) {
          log.warn({
            event: 'settings.linking.peerProfile.failed',
            chatIdPrefix: convHex.slice(0, 8),
            message: (err as Error).message,
          });
        }
      })();
    }
  }

  // [#2] Ship the newly-admitted device the decrypted history for the chats it
  // just joined, so it sees pre-link messages (it's its own MLS leaf and can't
  // decrypt them otherwise). Sealed to the QR ephemeral pubkey + signed by the
  // account identity. Best-effort, fire-and-forget — never blocks reconcile.
  if (newDevicePubBase64Url !== undefined && selfDeviceIdBase64Url !== undefined) {
    const admittedConvIds = outcomes
      .filter((o) => o.status === 'admitted')
      .map((o) => o.conversationId);
    if (admittedConvIds.length > 0) {
      void sendLinkingHistoryToDevice({
        recipientDeviceIdB64Url: newDeviceIdBase64Url,
        recipientPub: base64UrlToBytes(newDevicePubBase64Url),
        selfDeviceIdBytes: base64UrlToBytes(selfDeviceIdBase64Url),
        conversationIds: admittedConvIds,
      }).catch((err) => {
        log.warn({
          event: 'settings.linking.history.failed',
          message: (err as Error).message,
        });
      });
    }
  }
}

/**
 * Outer wrapper around `reconcileChatMembershipForLinkedDevice` that
 * RACES two trigger paths:
 *
 *   1. WS account-event push (`type === "device_added"`) sent by
 *      `auth.LinkVerify` via ws-fanout's `/internal/push/account`.
 *      Latency: ~100 ms after the new device finishes link-verify.
 *
 *   2. The polling loop inside `reconcileChatMembershipForLinkedDevice`,
 *      which hits `/v1/auth/sessions` on backoff. Latency: 1-8 s.
 *      Acts as a degradation fallback for the case where the existing
 *      device's WS is not connected at link time (deploy lag, tab
 *      backgrounded, transient network blip).
 *
 * The reconciler itself doesn't actually fire `addMembers` until it
 * sees the new device — when the WS push arrives, we still call the
 * same reconciler. Its internal `listSessions` poll then succeeds on
 * the very first call (because by then the BE has the row), so the
 * effective end-to-end latency for the push path is ~100 ms + one
 * `/v1/auth/sessions` round-trip.
 *
 * Returning early on the WS trigger could shave that one round-trip,
 * but the reconciler's "sessions probe" doubles as a sanity check
 * that the BE-side row IS there before we start consuming KPs and
 * issuing Commits — we keep it.
 */
// In-flight reconciles keyed by the device being admitted. The three triggers
// (resume-pending-link, server-ledger-drain, fast WS trigger) all fire around
// app load and would otherwise run the SAME admit concurrently — racing into
// "Duplicate signature key in proposals and group" and redundant work. Coalesce
// concurrent calls for the same device onto one promise.
const reconcileInFlight = new Map<string, Promise<void>>();

async function runReconcileWithFastTrigger(input: {
  accountId: string;
  newDeviceIdBase64Url: string;
  newDevicePubBase64Url?: string;
  selfDeviceIdBase64Url?: string;
  excludeConvHex?: Set<string>;
}): Promise<void> {
  const dedupeKey = input.newDeviceIdBase64Url;
  const inFlight = reconcileInFlight.get(dedupeKey);
  if (inFlight !== undefined) {
    log.info({
      event: 'settings.linking.reconcile.coalesced',
      deviceIdPrefix: dedupeKey.slice(0, 8),
    });
    return inFlight;
  }
  const run = runReconcileWithFastTriggerInner(input);
  reconcileInFlight.set(dedupeKey, run);
  try {
    await run;
  } finally {
    reconcileInFlight.delete(dedupeKey);
  }
}

async function runReconcileWithFastTriggerInner(input: {
  accountId: string;
  newDeviceIdBase64Url: string;
  newDevicePubBase64Url?: string;
  selfDeviceIdBase64Url?: string;
  excludeConvHex?: Set<string>;
}): Promise<void> {
  const { accountId, newDeviceIdBase64Url, newDevicePubBase64Url, selfDeviceIdBase64Url } = input;
  const excludeConvHex = input.excludeConvHex;

  // Register a one-shot WS listener BEFORE kicking the reconciler.
  // The reconciler's first sessions poll happens ~1 s in (initial
  // backoff), so even if the BE push arrives in <100 ms we'll still
  // catch it via the listener — the listener just lets us log /
  // shortcut faster than the polling timeline.
  const transport = getCurrentTransport();
  let wsTriggered = false;
  const sub = transport?.onAccountEvent((event) => {
    if (event['type'] !== 'device_added') return;
    if (event['device_id'] !== newDeviceIdBase64Url) return;
    wsTriggered = true;
    log.info({
      event: 'settings.linking.reconcile.ws_trigger',
      deviceIdPrefix: newDeviceIdBase64Url.slice(0, 8),
    });
  });

  try {
    await reconcileChatMembershipForLinkedDevice({
      accountId,
      newDeviceIdBase64Url,
      ...(newDevicePubBase64Url !== undefined ? { newDevicePubBase64Url } : {}),
      ...(selfDeviceIdBase64Url !== undefined ? { selfDeviceIdBase64Url } : {}),
      ...(excludeConvHex !== undefined ? { excludeConvHex } : {}),
    });
    // A sibling device just joined our conversations — re-arm the direct-profile
    // re-broadcast so our NEXT send announces our identity to it, letting it map
    // our sender device → account and render our messages as self.
    resetDirectProfileIntroSession();
  } finally {
    sub?.unsubscribe();
    if (!wsTriggered) {
      log.info({
        event: 'settings.linking.reconcile.via_polling',
        deviceIdPrefix: newDeviceIdBase64Url.slice(0, 8),
      });
    }
  }
}

/**
 * Resume an interrupted EXISTING-device link reconcile on app load. Reads the
 * durable `PendingOutgoingLink` record (written by `handleBuildLinkingTicket`
 * when this device posted a linking ticket), admits the new device to any
 * conversations not yet covered, and ships their history — then clears the
 * record once everything is admitted. No-op when nothing is pending. This is
 * what guarantees a newly-linked device is never left with zero synced
 * conversations because the user closed the Settings modal / tab before
 * admission completed. Idempotent and safe to call on every authenticated load.
 */
export async function resumePendingOutgoingLink(): Promise<void> {
  const record = await getPendingOutgoingLink();
  if (record === null) return;
  log.info({
    event: 'settings.linking.reconcile.resume',
    deviceIdPrefix: record.newDeviceId.slice(0, 8),
    alreadyAdmitted: record.admittedHex.length,
  });
  await runReconcileWithFastTrigger({
    accountId: record.accountId,
    newDeviceIdBase64Url: record.newDeviceId,
    ...(record.newDevicePub !== undefined ? { newDevicePubBase64Url: record.newDevicePub } : {}),
    ...(record.selfDeviceId !== undefined ? { selfDeviceIdBase64Url: record.selfDeviceId } : {}),
    excludeConvHex: new Set(record.admittedHex),
  });
}

/**
 * Drain the SERVER-coordinated admission ledger: admit EVERY sibling device of
 * this account that still needs admission to our conversations. Unlike
 * `resumePendingOutgoingLink` — which only the QR-initiating device runs, off a
 * client-local record — this runs on ANY online device, so a newly-linked
 * device gets a COMPLETE session even when the device that ran the QR flow is
 * offline or never finished. Idempotent (`reconcileChatMembershipForLinkedDevice`
 * skips conversations the sibling is already in). Best-effort; safe on every
 * authenticated load.
 */
export async function drainServerAdmissionLedger(): Promise<void> {
  const identity = await retrieveIdentity();
  const accountId = identity?.accountId;
  if (accountId === undefined || accountId.length === 0) return;
  const pending = await fetchPendingAdmissions();
  if (pending.length === 0) return;
  const selfBytes = await getSdkDeviceIdBytes();
  const selfDeviceId = selfBytes !== null ? bytesToBase64Url(selfBytes) : undefined;
  log.info({ event: 'settings.linking.ledger.drain', count: pending.length });
  for (const entry of pending) {
    await runReconcileWithFastTrigger({
      accountId,
      newDeviceIdBase64Url: entry.deviceId,
      ...(entry.historyDeliveryPubkey !== undefined
        ? { newDevicePubBase64Url: entry.historyDeliveryPubkey }
        : {}),
      ...(selfDeviceId !== undefined ? { selfDeviceIdBase64Url: selfDeviceId } : {}),
    });
  }
}

export function SettingsModalHost(): JSX.Element | null {
  const router = useRouter();
  const visible = router.modal.kind === 'settings';

  // Replay the guided tours from Help & About. The settings walkthrough runs
  // in place (the modal is already open). The app tour needs the shell, so we
  // close settings first, then kick it once the modal has unmounted.
  const replaySettingsTour = useCallback(() => {
    useTourReplay.getState().replay(TOUR_SETTINGS);
  }, []);
  const replayHomeTour = useCallback(() => {
    router.closeModal();
    setTimeout(() => useTourReplay.getState().replay(TOUR_HOME), 0);
  }, [router]);

  const [identity, setIdentity] = useState<LoadedIdentity | null>(null);
  // Server-canonical profile (bio/contact/discoverable/verified/avatar),
  // loaded on open to seed the Account pane editor.
  const [serverProfile, setServerProfile] = useState<ProfileResponse | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  // True while a freshly-picked avatar streams to S3 → drives the pane shimmer.
  const [avatarUploading, setAvatarUploading] = useState(false);
  // Local-marker snapshot read on every open so the Recovery pane's
  // "Last backup: …" / "No backup yet" badge reflects current state
  // (including any uploads from this session). `null` while the
  // initial read is in-flight; the pane treats `undefined` as "hide
  // the badge" but accepts the full discriminated union otherwise.
  const [backupStatus, setBackupStatus] = useState<RecoveryBackupStatus | undefined>(undefined);
  const [kycStatus, setKycStatus] = useState<KycStatus>('none');
  // Inquiry id of the in-flight KYC flow → renders the embedded Persona
  // modal. Null when no verification is open.
  const [kycInquiryId, setKycInquiryId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Storage pane — media-cache usage + clear. Recomputed whenever the modal opens.
  const [storageUsage, setStorageUsage] = useState<MediaCacheUsage | null>(null);
  const [clearingStorage, setClearingStorage] = useState(false);
  const refreshStorage = useCallback(() => {
    setStorageUsage(null);
    void computeMediaCacheUsage()
      .then(setStorageUsage)
      .catch(() => setStorageUsage(null));
  }, []);
  useEffect(() => {
    if (visible) refreshStorage();
  }, [visible, refreshStorage]);
  const handleClearMediaCache = useCallback(async () => {
    setClearingStorage(true);
    try {
      await clearMediaCache();
    } finally {
      setClearingStorage(false);
      refreshStorage();
    }
  }, [refreshStorage]);

  // Subscribe to the preferences store so the Appearance pane re-
  // renders whenever the host (or any other surface) tweaks a
  // preference. `usePreferences` is the same hook the legacy web
  // settings flow uses — sticking to it keeps the two screens in
  // sync and avoids duplicating the storage layer.
  const preferences = usePreferences();

  const handleThemeChange = useCallback((next: AppearanceTheme) => {
    // `preferencesStore.setTheme` persists to localStorage AND
    // flips `data-theme="dark"` + the `dark` class on <html> +
    // `color-scheme`, all in lockstep. See the docstring on
    // `applyTheme` in PreferencesStore.ts for why all three need
    // to move together.
    preferencesStore.setTheme(next);
  }, []);

  const handleAccentChange = useCallback((next: string) => {
    preferencesStore.setAccentId(next);
  }, []);

  const handleBackgroundChange = useCallback((next: AppearanceBackground) => {
    preferencesStore.setChatBackground(next);
  }, []);

  const handleFontScaleChange = useCallback((next: number) => {
    preferencesStore.setFontScale(next);
  }, []);

  // Notification preferences. OS-permission state is tracked locally so the
  // pane can show the "Enable in system" CTA / blocked hint until granted.
  const [osPermission, setOsPermission] = useState<
    'granted' | 'denied' | 'default' | 'unsupported'
  >(() => osNotificationPermission());
  const handleRequestOsPermission = useCallback(() => {
    // If the browser already denied, requestPermission() resolves immediately
    // WITHOUT a prompt — so the button would do nothing. The pane shows the
    // unblock hint for that case instead; we still refresh the state here.
    void requestOsNotificationPermission().then(() => {
      setOsPermission(osNotificationPermission());
    });
  }, []);
  const notificationsSettings = useMemo(
    () => ({
      notificationsEnabled: preferences.notificationsEnabled,
      onNotificationsEnabledChange: (v: boolean) => preferencesStore.setNotificationsEnabled(v),
      osNotificationsEnabled: preferences.osNotificationsEnabled,
      onOsNotificationsEnabledChange: (v: boolean) => {
        preferencesStore.setOsNotificationsEnabled(v);
        if (v && !osNotificationsGranted()) handleRequestOsPermission();
      },
      osPermissionGranted: osPermission === 'granted',
      osPermissionBlocked: osPermission === 'denied',
      onRequestOsPermission: handleRequestOsPermission,
      preview: preferences.notificationPreview,
      onPreviewChange: (v: 'full' | 'name' | 'none') => preferencesStore.setNotificationPreview(v),
      inAppSounds: preferences.inAppSounds,
      onInAppSoundsChange: (v: boolean) => preferencesStore.setInAppSounds(v),
      notifyOnlyWhenUnfocused: preferences.notifyOnlyWhenUnfocused,
      onNotifyOnlyWhenUnfocusedChange: (v: boolean) =>
        preferencesStore.setNotifyOnlyWhenUnfocused(v),
      dndEnabled: preferences.dndEnabled,
      onDndEnabledChange: (v: boolean) => preferencesStore.setDndEnabled(v),
      dndStart: preferences.dndStart,
      dndEnd: preferences.dndEnd,
      onDndWindowChange: (s: string, e: string) => preferencesStore.setDndWindow(s, e),
    }),
    [preferences, osPermission, handleRequestOsPermission],
  );

  // Behaviour preferences (Enter-to-send, auto-download) — persisted to the
  // web preferences singleton so the toggle takes effect (the composer reads
  // `enterToSend` via `<ChatRoom pressEnterToSend>` in WebChatRoom).
  const preferencesSettings = useMemo(
    () => ({
      enterToSend: preferences.enterToSend,
      onEnterToSendChange: (v: boolean) => preferencesStore.setEnterToSend(v),
      autoDownloadOnCellular: preferences.autoDownloadOnCellular,
      onAutoDownloadOnCellularChange: (v: boolean) => preferencesStore.setAutoDownloadOnCellular(v),
    }),
    [preferences],
  );

  // Pull the persisted display name / device id whenever the modal
  // opens. Cheap enough to re-fetch each open — keeps the UI in sync
  // after an in-app rename without plumbing a store subscription.
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    void (async () => {
      const persisted = await retrieveIdentity();
      if (cancelled) return;
      if (persisted) {
        setIdentity({
          displayName: persisted.displayName,
          deviceId: persisted.deviceId,
          accountId: persisted.accountId,
        });
      } else {
        setIdentity(null);
      }
    })();
    void (async () => {
      const status = await readRecoveryBackupStatus();
      if (cancelled) return;
      // `null` from the helper means "no marker yet" → render the
      // "never set up" state in the pane via the explicit `'never'`
      // tag, so the badge tells the user they need to act.
      setBackupStatus(status ?? { kind: 'never' });
    })();
    // Server-canonical profile → seeds the Account editor (bio, contact,
    // discoverable, verified) and resolves the avatar media id to a URL.
    void (async () => {
      try {
        const profile = await getProfile();
        if (cancelled) return;
        setServerProfile(profile);
        let resolvedAvatar: string | undefined;
        if (profile.avatarMediaId !== null) {
          try {
            const url = await fetchMediaDownloadUrl(profile.avatarMediaId);
            resolvedAvatar = url;
            if (!cancelled) setAvatarUrl(url);
          } catch {
            if (!cancelled) setAvatarUrl(undefined);
          }
        } else {
          setAvatarUrl(undefined);
        }
        // Keep the shared self-profile store in sync (sidebar + chat avatar).
        // Only set avatarUrl when we know it (resolved, or null when no avatar).
        useSelfProfile.getState().setProfile({
          displayName: profile.displayName,
          avatarMediaId: profile.avatarMediaId,
          ...(resolvedAvatar !== undefined
            ? { avatarUrl: resolvedAvatar }
            : profile.avatarMediaId === null
              ? { avatarUrl: null }
              : {}),
        });
      } catch {
        if (!cancelled) setServerProfile(null);
      }
    })();
    // KYC status — best-effort; section stays visible with 'none' on error.
    void (async () => {
      try {
        const { status } = await getKycStatus();
        if (!cancelled) setKycStatus(status);
      } catch {
        // API error or Persona not configured — leave at 'none' so the
        // section is visible and the user can attempt to start verification.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  const handleDisplayNameSaved = useCallback((next: string) => {
    setIdentity((prev) => (prev ? { ...prev, displayName: next } : prev));
    // Mirror the new value into the persisted identity row so a
    // reload doesn't reset the field to the stale display name.
    void (async () => {
      const persisted = await retrieveIdentity();
      if (persisted) {
        await storeIdentity({ ...persisted, displayName: next });
      }
    })();
  }, []);

  // Sign-out destroys ALL local chats (the device's MLS state is wiped). If
  // this is the user's ONLY device and they have NO recovery backup, those
  // chats are gone for good — warn + require explicit confirmation first.
  // Returns true to proceed, false to abort. Never blocks when we can't tell
  // (best-effort) — better to allow sign-out than to trap the user.
  const confirmSignOutIfRisky = useCallback(async (): Promise<boolean> => {
    try {
      // Time-bound the risk probe: this is only a courtesy "you have no backup"
      // confirmation, so it must NEVER hang the sign-out. If the network probe
      // (`listSessions`) stalls, we resolve to "couldn't assess" and proceed —
      // a stalled probe must not prevent the sign-out (and its guaranteed
      // `forceCleanReload`) from running.
      const probe = Promise.all([
        listSessions().catch(() => [] as Awaited<ReturnType<typeof listSessions>>),
        readRecoveryBackupStatus().catch(() => null),
      ]);
      const result = await Promise.race([
        probe,
        new Promise<null>((resolve) => {
          setTimeout(() => resolve(null), 2500);
        }),
      ]);
      if (result === null) return true; // couldn't assess in time — don't block
      const [sessions, backup] = result;
      const soleDevice = sessions.length <= 1;
      const hasBackup = backup?.kind === 'completed';
      if (soleDevice && !hasBackup && typeof window !== 'undefined') {
        return window.confirm(
          "This is your only device and you don't have a recovery backup.\n\n" +
            'Signing out permanently deletes all your chats on this device — they cannot be recovered.\n\n' +
            'To keep them, cancel and create a backup first in Settings → Account recovery.\n\n' +
            'Sign out anyway?',
        );
      }
    } catch {
      /* couldn't determine risk — don't block sign-out */
    }
    return true;
  }, []);

  // Shared local-wipe + GUARANTEED clean-slate reload for EVERY settings
  // sign-out path. This is the load-bearing fix: `clearAllLocalSessionState`
  // ({mode:'logout'}) tears down the SDK session, which synchronously flips the
  // session store to `unauthenticated`. That fires App.tsx's COMPETING
  // `goUnauthenticated('sessionStore.unauthenticated')` (a client-side redirect
  // with NO reload) and, in practice, leaves the original
  // `clearAllLocalSessionState` promise pending forever — so the `finally` that
  // called `forceCleanReload` never ran. We therefore TIME-BOX the graceful
  // teardown so it can never block, ALWAYS delete the IndexedDB stores (hardWipe
  // — this is the part that actually clears tokens + identity so the next boot
  // can't re-auth), then force the hard reload (which tears down the SDK Web
  // Worker + WASM regardless). Every step is best-effort; the reload always runs.
  const wipeAndReload = useCallback(
    async (reason: string): Promise<void> => {
      // Surface progress while the (possibly multi-second) teardown + IDB
      // deletion runs, before `forceCleanReload` navigates: a full-screen
      // "Cleaning…" overlay (vanilla DOM, so it survives the React unmount the
      // teardown triggers) plus the browser-tab title. The hard reload loads a
      // fresh document, so both reset on their own.
      if (typeof document !== 'undefined') {
        document.title = 'Cleaning…';
      }
      showCleaningOverlay('Cleaning…');
      try {
        await Promise.race([
          clearAllLocalSessionState({ reason, mode: 'logout' }).catch(() => undefined),
          new Promise<void>((resolve) => {
            setTimeout(resolve, 1500);
          }),
        ]);
      } catch {
        /* best-effort — never block the reload on a graceful-teardown hiccup */
      }
      // TIME-BOX the hard wipe too. Every step inside is `.catch`-wrapped, but that
      // only guards against REJECTION — a never-RESOLVING await (a blocked IDB open
      // / delete while the SDK worker or a live tab still holds a handle) would hang
      // here forever, and since `forceCleanReload` runs AFTER this await, the user
      // is stranded on the "Cleaning…" overlay with no reload (the reported bug).
      // The reload is the load-bearing part and `forceCleanReload` re-runs the hard
      // wipe under its own deadline anyway, so racing this one loses no cleanup.
      await Promise.race([
        hardWipeBrowserAuthStorage().catch(() => undefined),
        new Promise<void>((resolve) => {
          setTimeout(resolve, 1500);
        }),
      ]);
      try {
        router.closeModal();
      } catch {
        /* non-fatal — the reload replaces the whole page regardless */
      }
      await forceCleanReload(reason);
    },
    [router],
  );

  // PRESERVE-RESUME sign-out for THIS device — clears the SESSION (tokens → login
  // screen) but KEEPS this device's MLS identity, group state, message history,
  // and sync cursors, so re-signing in on this browser resumes the EXACT same MLS
  // leaf: all conversations + messages present, live delivery, and any messages a
  // peer sent while signed out are fetched from the preserved cursors (catch-up
  // sync) and decrypted with the surviving leaf. Does NOT nuke IDB
  // (`hardWipeBrowserAuthStorage`/`forceCleanReload`) — a PLAIN reload. The prior
  // re-login `identity_mismatch` is now prevented by the BE change that excludes
  // the current device from the active-device check (`BindIdentityPubkey`), so a
  // same-device re-sign-in re-pins/matches cleanly and the leaf resumes.
  const preserveAndReload = useCallback(
    async (reason: string): Promise<void> => {
      if (typeof document !== 'undefined') {
        document.title = 'Signing out…';
      }
      showCleaningOverlay('Signing out…');
      try {
        await Promise.race([
          clearAllLocalSessionState({ reason, mode: 'logout-preserve' }).catch(() => undefined),
          new Promise<void>((resolve) => {
            setTimeout(resolve, 1500);
          }),
        ]);
      } catch {
        /* best-effort — never block the reload */
      }
      try {
        router.closeModal();
      } catch {
        /* non-fatal */
      }
      try {
        if (typeof window !== 'undefined') window.location.reload();
      } catch {
        /* best-effort */
      }
    },
    [router],
  );

  // Sign-out for the current device only — Lucide LogOut icon in
  // the sidebar account card. Revokes this device's refresh token
  // server-side, then wipes local state + forces a clean-slate reload.
  const handleSignOutCurrentDevice = useCallback(async (): Promise<void> => {
    // PRESERVE-RESUME sign-out (default): no risky-sign-out warning — it does NOT
    // lose chats (identity + MLS state + history stay), so re-signing in on this
    // device resumes the same leaf with missed messages. The zero-residue path is
    // "Sign out & Erase Device".
    try {
      const persisted = await retrieveIdentity();
      const refresh = await retrieveRefreshToken();
      await unregisterWebPush().catch(() => undefined);
      if (persisted && refresh) {
        try {
          await logoutCurrentDevice({
            refreshToken: refresh,
            deviceId: persisted.deviceId,
          });
        } catch (err) {
          // Treat BE errors as soft — even if the token-revoke
          // roundtrip fails (network, 401 already, etc.) we
          // still want to clear the local session so the user
          // isn't stranded in a half-authed state.
          log.warn({ event: 'settings.signOut.revoke_failed' }, (err as Error).message);
        }
      }
    } catch (err) {
      log.warn({ event: 'settings.signOut.preWipeFailed' }, (err as Error).message);
    } finally {
      // PRESERVE-RESUME: keep this device's identity + MLS state + history so
      // re-signing in resumes the same leaf (missed messages via catch-up sync).
      log.info({ event: 'settings.signOut.completed', preserve: true });
      await preserveAndReload('signOut.current.preserve');
    }
  }, [preserveAndReload]);

  // ERASE THIS DEVICE — the zero-residue counterpart to the preserve-resume
  // sign-out above: for a shared/at-risk browser, destroy every local
  // auth/identity/MLS/chat byte and hard-reload (the old full-wipe behaviour).
  // Re-sign-in mints a fresh leaf (link / recover / peer-readmit). Keeps the
  // sole-device backup warning since local chats ARE lost.
  const handleEraseThisDevice = useCallback(async (): Promise<void> => {
    if (!(await confirmSignOutIfRisky())) return;
    try {
      const persisted = await retrieveIdentity();
      const refresh = await retrieveRefreshToken();
      await unregisterWebPush().catch(() => undefined);
      if (persisted && refresh) {
        try {
          await logoutCurrentDevice({ refreshToken: refresh, deviceId: persisted.deviceId });
        } catch (err) {
          log.warn({ event: 'settings.eraseDevice.revoke_failed' }, (err as Error).message);
        }
      }
    } catch (err) {
      log.warn({ event: 'settings.eraseDevice.preWipeFailed' }, (err as Error).message);
    } finally {
      log.info({ event: 'settings.eraseDevice.completed' });
      await wipeAndReload('eraseDevice');
    }
  }, [confirmSignOutIfRisky, wipeAndReload]);

  const handleSignOutAllDevices = useCallback(async (): Promise<void> => {
    if (!(await confirmSignOutIfRisky())) return;
    try {
      const persisted = await retrieveIdentity();
      const refresh = await retrieveRefreshToken();
      await unregisterWebPush().catch(() => undefined);
      if (persisted && refresh) {
        try {
          // "Sign out from all devices" revokes every device + refresh token on
          // the account server-side. Re-sign-in re-pins a fresh identity (the
          // account now has no active devices). See handleSignOutCurrentDevice.
          await logoutAllDevices({
            refreshToken: refresh,
            deviceId: persisted.deviceId,
          });
        } catch (err) {
          // Same softness as the single-device path — if the BE roundtrip
          // fails we still nuke local state so the user can recover.
          log.warn({ event: 'settings.sessions.signOutAll.revoke_failed' }, (err as Error).message);
        }
      }
    } catch (err) {
      log.warn({ event: 'settings.sessions.signOutAll.preWipeFailed' }, (err as Error).message);
    } finally {
      log.info({ event: 'settings.sessions.signOutAll.completed' });
      await wipeAndReload('signOut.all');
    }
  }, [confirmSignOutIfRisky, wipeAndReload]);

  const handleUpdateProfile = useCallback(
    async (input: {
      displayName?: string;
      bio?: string;
      jobTitle?: string;
      companyName?: string;
      contactEmail?: string;
      contactPhone?: string;
    }): Promise<void> => {
      // PATCH /v1/me/profile is the authoritative write — the returned
      // `ProfileResponse` carries the server-canonical values (the BE may
      // trim/normalize). Push those into the local profile snapshot + identity
      // row so other surfaces stay in sync.
      const profile = await updateProfile(input);
      setServerProfile(profile);
      useSelfProfile.getState().setProfile({ displayName: profile.displayName });
      setIdentity((prev) => (prev ? { ...prev, displayName: profile.displayName } : prev));
      const persisted = await retrieveIdentity();
      if (persisted) {
        await storeIdentity({ ...persisted, displayName: profile.displayName });
      }
      // Realtime multi-device: nudge our OTHER devices to re-pull the profile.
      void sendSelfProfileChanged(
        (useSessionStore.getState().status as { client?: unknown }).client ?? null,
      );
    },
    [],
  );

  const handleToggleDiscoverable = useCallback(async (next: boolean): Promise<void> => {
    const profile = await updateProfile({ discoverable: next });
    setServerProfile(profile);
  }, []);

  // Initiate Persona KYC. Creates the inquiry server-side (POST /v1/me/kyc)
  // then opens it in the EMBEDDED in-page modal (`PersonaKycModal`) — no new
  // tab/popup. Status flips to 'pending' optimistically; the webhook + the
  // polling effect above resolve it to approved/declined, and the modal's
  // onComplete refetches immediately for instant feedback.
  const handleKycStart = useCallback(async (): Promise<void> => {
    const { inquiryId } = await startKyc();
    setKycStatus('pending');
    setKycInquiryId(inquiryId);
  }, []);

  const refreshKycStatus = useCallback(async (): Promise<void> => {
    try {
      const { status } = await getKycStatus();
      setKycStatus(status);
    } catch {
      /* best-effort */
    }
  }, []);

  const handleLoadDevices = useCallback(async () => {
    const persisted = await retrieveIdentity();
    if (!persisted) {
      return { currentDeviceId: null, devices: [] as const };
    }
    const wire = await api.get<DevicesWireResponse>(`/v1/users/${persisted.accountId}/devices`);
    return {
      currentDeviceId: persisted.deviceId,
      devices: wire.devices.map((d) => ({
        deviceId: d.device_id,
        signaturePublicKeyRef: d.signature_public_key_ref,
      })),
    };
  }, []);

  // Sessions pane uses the auth-layer device list (every non-revoked
  // device on the account), distinct from the Encryption pane's MLS
  // leaf list above. `GET /v1/auth/sessions` returns the rows from
  // `auth.devices` for the calling account.
  const handleLoadSessions = useCallback(async (): Promise<readonly SessionDevice[]> => {
    return listSessions();
  }, []);

  // Multi-select bulk revoke. Combines:
  //   1. SDK-side `revokeDevice` — emits MLS Remove commits to every
  //      group the target device participates in, so peers stop
  //      encrypting to it.
  //   2. BE `POST /v1/devices/{id}/revoke` — invalidates the device's
  //      KeyPackage pool + refuses future envelopes from its JWT.
  //
  // Step 2 also kills the refresh token, so we don't need a separate
  // `logoutDevices` call any more. Failures in step 1 are logged but
  // do not block step 2 — the auth-layer kill is the security-critical
  // half (it stops new envelopes flowing); the MLS commits are about
  // forward secrecy, which falls through to the SDK's next epoch
  // change if we miss it here.
  const handleSignOutDevices = useCallback(
    async (deviceIds: readonly string[]): Promise<void> => {
      const persisted = await retrieveIdentity();
      if (!persisted) {
        throw new Error('Not signed in.');
      }
      const transport = getCurrentTransport();
      const session = useSessionStore.getState();
      const sdkClient =
        session.status.kind === 'authenticated'
          ? (session.status.client as unknown as {
              revokeDevice?: (id: Uint8Array) => Promise<unknown>;
            })
          : null;
      for (const id of deviceIds) {
        try {
          if (sdkClient && typeof sdkClient.revokeDevice === 'function') {
            const idBytes = base64UrlToBytes(id);
            await sdkClient.revokeDevice(idBytes);
          }
        } catch (mlsErr) {
          log.warn(
            {
              event: 'settings.sessions.revokeDevice.mls_failed',
              message: (mlsErr as Error).message,
              deviceIdPrefix: id.slice(0, 8),
            },
            'MLS revoke step failed — continuing with auth-layer revoke',
          );
        }
        try {
          // Prefer the transport helper because it shares the same api
          // interceptor (auth header, retry, error wrapping); fall back
          // to the bare client otherwise.
          if (transport && typeof transport.revokeDeviceRemote === 'function') {
            await transport.revokeDeviceRemote(base64UrlToBytes(id));
          } else {
            await revokeDevice(id);
          }
        } catch (authErr) {
          log.error(
            {
              event: 'settings.sessions.revokeDevice.auth_failed',
              message: (authErr as Error).message,
              deviceIdPrefix: id.slice(0, 8),
            },
            'auth-layer revoke failed',
          );
          throw authErr;
        }
      }
      log.info({
        event: 'settings.sessions.signOutDevices.completed',
        count: deviceIds.length,
      });

      // If this revoke INCLUDED the current device (the dropdown "Sign out"
      // fallback routes the current device here when `onSignOutCurrentDevice`
      // isn't wired), it is a sign-out of THIS browser — so it must run the
      // same full local wipe + guaranteed clean-slate reload as the dedicated
      // current-device path. Revoking only OTHER devices leaves this session
      // intact (no reload).
      if (deviceIds.includes(persisted.deviceId)) {
        await wipeAndReload('signOut.devices.self');
      }
    },
    [wipeAndReload],
  );

  // Linking — existing-device side. Builds a `LinkingTicket` addressed
  // to the new device's HPKE pubkey, seals it client-side (Ping's BE
  // never sees plaintext), and POSTs the sealed bytes for the new
  // device to fetch by id.
  const handleBuildLinkingTicket = useCallback(
    async (input: LinkingBuildInput): Promise<LinkingBuildResult> => {
      const session = useSessionStore.getState();
      if (session.status.kind !== 'authenticated') {
        throw new Error('Not signed in.');
      }
      const client = session.status.client as unknown as {
        buildLinkingTicket: (id: Uint8Array, kp: Uint8Array) => Promise<unknown>;
        exportIdentity: () => Promise<Uint8Array>;
      };
      const MC = MessagingClient as unknown as {
        sealLinkingTicket: (ticket: unknown, pub: Uint8Array) => Promise<Uint8Array>;
        hpkeSeal: (plaintext: Uint8Array, pub: Uint8Array, info: Uint8Array) => Promise<Uint8Array>;
      };
      const deviceId = base64UrlToBytes(input.newDeviceIdBase64Url);
      const kp = base64UrlToBytes(input.newDeviceKeyPackageBase64Url);
      const pub = base64UrlToBytes(input.newDevicePubBase64Url);
      const ticket = await client.buildLinkingTicket(deviceId, kp);
      const sealed = await MC.sealLinkingTicket(ticket, pub);

      // Auth envelope: a SECOND HPKE-sealed bundle (info string
      // `ping/link-auth/v1`) sealed against the SAME new-device eph
      // pubkey. Carries this device's auth-layer
      // `__user_identity_pubkey` plus a BE-format
      // `device_binding_sig` over the new device's id. The new
      // device opens this with the eph priv it has in
      // sessionStorage and feeds the cleartext to
      // `POST /v1/auth/link-verify` so it can mint tokens WITHOUT
      // an OTP roundtrip on the new device. Without this, the new
      // device would land on the welcome screen and the user would
      // hit `identity_mismatch` trying to OTP-sign in (the account
      // is already bound to THIS device's user_identity_pubkey).
      const userIdentityPubkey = await getUserIdentityPubkeyBytes();
      const authBindingSig = await signDeviceBinding({ overrideDeviceId: deviceId });
      // Shared-identity linking (v2): transfer THIS device's SDK account
      // identity to the new device so both share one MLS `user_id` — the basis
      // for cross-device self-attribution (`sender_user_id === client.userId()`
      // for any of the account's own devices). `exportIdentity()` is the
      // authoritative seed backing `client.userId()`. SECRET — only travels
      // inside the HPKE-sealed `authSealed` bundle, device-to-own-device.
      const identitySeed = await client.exportIdentity();
      const authPlaintext = new TextEncoder().encode(
        JSON.stringify({
          v: 2,
          user_pubkey: bytesToUrlBase64(userIdentityPubkey),
          binding_sig: bytesToUrlBase64(authBindingSig),
          identity_seed: bytesToUrlBase64(identitySeed),
        }),
      );
      const authInfo = new TextEncoder().encode('ping/link-auth/v1');
      // Seal via the SDK's HPKE (shared cross-platform primitive) instead of the
      // pure-JS `hpkeSeal` — the hand-rolled JS/Swift impls drifted and broke
      // iOS↔web. Paired with `MessagingClient.hpkeOpen` on the new device.
      const authSealed = await MC.hpkeSeal(authPlaintext, pub, authInfo);

      // SHA-256(eph_pk) — when the new device opted into the
      // pre-auth WS push, ws-fanout uses this hash to route the
      // sealed bytes directly to its open `/v1/linking/stream`
      // subscription. The BE silently no-ops when no subscriber
      // is connected, so omitting `delivery_pubkey_hash` would
      // simply fall back to the legacy `GET /v1/linking/tickets/{id}`
      // flow. Computing it unconditionally keeps the wire shape
      // consistent and gives the new device the option.
      const deliveryHash = await pubkeyHashB64Url(pub);
      const resp = await postLinkingTicket({
        sealed: bytesToUrlBase64(sealed),
        auth_sealed: bytesToUrlBase64(authSealed),
        delivery_pubkey_hash: deliveryHash,
      });
      log.info({
        event: 'settings.linking.ticket.built',
        ticketIdPrefix: resp.ticket_id.slice(0, 8),
        pushDelivered: resp.push_delivered ?? null,
      });

      // Fire-and-forget chat-membership reconciliation. The new
      // device's MLS-state import (via catchup_snapshot →
      // `importStateSnapshot`) restores keys locally, but the BE's
      // `auth.conversation_members` table still records ONLY the
      // existing device for each chat. Without explicit Add commits
      // routed through the BE, every chat the new device tries to
      // fetchSince gets 404'd and orphan-pruned (exactly what the
      // user just hit).
      //
      // We fix this by calling `addMembers` for each chat the
      // existing device is in — once the new device has finished
      // its own bootstrap + KP-replenish. The flow:
      //   1. Poll `/v1/auth/sessions` until the new device appears
      //      (its `link-verify` completed → tokens minted).
      //   2. Poll `/v1/devices/{accountId}` until the new device
      //      has a fresh KP available (its `replenishKeyPackages`
      //      finished).
      //   3. For each chat (filtered to !is_device_group), claim a
      //      fresh KP for the new device and `addMembers`. Each
      //      call sends a Commit + Welcome via the transport — the
      //      Commit updates BE membership, the Welcome auto-joins
      //      the new device's SDK on its WS subscribe.
      //
      // Not awaited: the LinkingPane's "Paired ✓" success is driven
      // by `handleWatchForLinkPaired` on the modal lifecycle; this
      // reconciler runs to completion (or its own timeout) regardless
      // of whether the user closes the modal.
      const accountIdForReconcile = identity?.accountId;
      if (accountIdForReconcile !== undefined && accountIdForReconcile.length > 0) {
        // Persist a durable record FIRST so that if the user closes the modal
        // or the tab before admission completes, the reconcile resumes on the
        // next app load (`resumePendingOutgoingLink`). The in-tab run below is
        // the fast path; this is the never-lose-a-link backstop.
        void savePendingOutgoingLink({
          accountId: accountIdForReconcile,
          newDeviceId: input.newDeviceIdBase64Url,
          ...(input.newDevicePubBase64Url !== undefined
            ? { newDevicePub: input.newDevicePubBase64Url }
            : {}),
          ...(identity?.deviceId !== undefined ? { selfDeviceId: identity.deviceId } : {}),
          admittedHex: [],
        });
        // Race two triggers for the reconciler, take whichever fires
        // first:
        //   1. WS push: `auth.LinkVerify` on the BE publishes a
        //      `{type:"device_added", device_id, ...}` event to
        //      ws-fanout's `/internal/push/account`. We register a
        //      one-shot listener on the transport that resolves the
        //      moment we see the event for OUR expected device id.
        //   2. Polling: the existing 2-minute backoff loop is the
        //      degradation fallback for the "WS disconnected at link
        //      time" case.
        // Whichever resolves first triggers the chat addMembers
        // pass; the other is cancelled.
        void runReconcileWithFastTrigger({
          accountId: accountIdForReconcile,
          newDeviceIdBase64Url: input.newDeviceIdBase64Url,
          // [#2] thread the QR ephemeral pubkey + this device's id so the
          // reconciler can ship decrypted history to the new device after admit.
          newDevicePubBase64Url: input.newDevicePubBase64Url,
          ...(identity?.deviceId !== undefined ? { selfDeviceIdBase64Url: identity.deviceId } : {}),
        }).catch((err) => {
          log.warn({
            event: 'settings.linking.reconcile.failed',
            message: (err as Error).message,
          });
        });
      }

      return {
        ticketId: resp.ticket_id,
        expiresAt: resp.expires_at,
        ...(resp.push_delivered !== undefined ? { pushDelivered: resp.push_delivered } : {}),
      };
    },
    [identity?.accountId, identity?.deviceId],
  );

  // Linking — realtime watcher. After `handleBuildLinkingTicket`
  // posts the sealed ticket, the LinkingPane invokes this with the
  // new device's id and an `AbortSignal`. We poll
  // `GET /v1/auth/sessions` on a short exponential backoff until the
  // BE shows the new device (it appears as soon as the new device
  // finishes `POST /v1/auth/link-verify`). On detection we resolve
  // with the device label; on timeout we resolve null so the pane
  // can keep its fallback affordance visible.
  //
  // 60-second budget matches the LinkingTicket TTL (5 min) loosely —
  // the legitimate new device should redeem in <10 s. We don't keep
  // polling forever because the user might background the tab.
  const handleWatchForLinkPaired = useCallback(
    async (
      input: { newDeviceIdBase64Url: string },
      signal: AbortSignal,
    ): Promise<{ label: string } | null> => {
      const targetId = input.newDeviceIdBase64Url;
      const deadline = Date.now() + 60_000;
      // Backoff: 1s, 1s, 1s, 2s, 2s, 3s … capped at 4s. Reaches the
      // new device within the first 2 polls in the happy path.
      const backoffs = [1000, 1000, 1000, 2000, 2000, 3000, 4000];
      let attempt = 0;
      while (!signal.aborted && Date.now() < deadline) {
        try {
          const sessions = await listSessions();
          if (signal.aborted) return null;
          const hit = sessions.find((s) => s.deviceId === targetId);
          if (hit) {
            log.info({
              event: 'settings.linking.paired.detected',
              deviceIdPrefix: targetId.slice(0, 8),
              label: hit.label,
            });
            return { label: hit.label || 'New device' };
          }
        } catch (err) {
          log.debug({
            event: 'settings.linking.paired.poll_failed',
            message: (err as Error).message,
          });
        }
        const delay = backoffs[Math.min(attempt, backoffs.length - 1)] ?? 4000;
        await new Promise<void>((resolve) => {
          if (signal.aborted) {
            resolve();
            return;
          }
          const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
          }, delay);
          const onAbort = (): void => {
            clearTimeout(timer);
            resolve();
          };
          signal.addEventListener('abort', onAbort, { once: true });
        });
        attempt += 1;
      }
      return null;
    },
    [],
  );

  // Linking — new-device side. Fetches the sealed ticket from the BE,
  // opens it with the local link priv key, and consumes it via the SDK
  // (which imports state into the running client).
  //
  // For now this looks up the linking priv key from a known sessionStorage
  // slot (`ping.linkingPriv.b64u`) set by the auth-flow's "Link this
  // device" screen. When that screen ships it'll write the key there;
  // until then this branch fails fast with a clear error so QA knows
  // they're testing without the prereq.
  const handleAcceptLinkingTicket = useCallback(
    async (input: LinkingAcceptInput): Promise<void> => {
      const privB64Url =
        typeof sessionStorage !== 'undefined'
          ? sessionStorage.getItem('ping.linkingPriv.b64u')
          : null;
      if (!privB64Url) {
        throw new Error(
          "No linking private key in sessionStorage. Run the 'Link this device' auth-flow first, or set ping.linkingPriv.b64u manually for testing.",
        );
      }
      const session = useSessionStore.getState();
      if (session.status.kind !== 'authenticated') {
        throw new Error('Not signed in.');
      }
      const client = session.status.client as unknown as {
        consumeLinkingTicket: (t: unknown) => Promise<void>;
      };
      const MC = MessagingClient as unknown as {
        openLinkingTicket: (sealed: Uint8Array, priv: Uint8Array) => Promise<unknown>;
      };
      const sealedResp = await getLinkingTicket(input.ticketId);
      const sealed = base64UrlToBytes(sealedResp.sealed);
      const priv = base64UrlToBytes(privB64Url);
      const ticket = await MC.openLinkingTicket(sealed, priv);
      await client.consumeLinkingTicket(ticket);
      log.info({
        event: 'settings.linking.ticket.consumed',
        ticketIdPrefix: input.ticketId.slice(0, 8),
      });
    },
    [],
  );

  // Recovery — encrypt the local identity export with the user's
  // passphrase and POST as a sealed blob. Plain text never leaves
  // this device. After a successful upload we stamp the local marker
  // so the same status surfaces on next mount and the post-signup
  // gate in App.tsx knows not to re-prompt.
  const handleUploadRecoveryBlob = useCallback(async (input: RecoveryUploadInput) => {
    const identityExport = await identityManager.unwrapIdentity();
    if (!identityExport) {
      throw new Error('No identity to back up.');
    }
    const ciphertext = await encryptRecoveryBlob(identityExport, input.passphrase);
    await postRecoveryBlob({ ciphertext });
    const status: RecoveryBackupStatus = {
      kind: 'completed',
      uploadedAt: Date.now(),
      bytes: identityExport.length,
    };
    await db.settings_kv.put({
      key: RECOVERY_BACKUP_KV_KEY,
      value: status,
      updatedAt: Date.now(),
    });
    setBackupStatus(status);
    log.info({
      event: 'settings.recovery.uploaded',
      bytes: identityExport.length,
    });
    return { bytes: identityExport.length };
  }, []);

  // V3 reveal: surface the locally-stored mnemonic from the background
  // post-signup backup so the user can write it down. Returns null when
  // nothing is stored (already saved + purged, or rotated since).
  const handleRevealRecoveryMnemonic = useCallback(async (): Promise<string | null> => {
    return retrieveRecoveryMnemonic();
  }, []);

  // V3 confirm: the user wrote the phrase down → persist the flag and
  // PURGE the stored mnemonic so the plaintext-recoverable secret no
  // longer lives on disk.
  const handleConfirmRecoveryMnemonicSaved = useCallback(async (): Promise<void> => {
    await confirmRecoveryMnemonicSaved();
    log.info({ event: 'settings.recovery.mnemonicSavedConfirmed' });
  }, []);

  // V3 rotate: generate a fresh mnemonic, build + upload a NEW V3 blob
  // (SDK Argon2id backup + HKDF-wrapped auth companion), stamp the local
  // marker, and return the new phrase so the pane can display it for the
  // user to write down. Replaces the legacy PBKDF2 `encryptRecoveryBlob`
  // rotate path — a backup made here is restorable by the V3 restore
  // flow. The returned mnemonic is NOT stashed locally: the rotate UI
  // shows it immediately and gates a confirm, so there's no later
  // reveal to support (and nothing to purge).
  const handleRotateRecoveryKey = useCallback(async (): Promise<string> => {
    const mnemonic = await MessagingClient.generateMnemonicPhrase();
    const ciphertext = await buildRecoveryBlobV3(mnemonic);
    await postRecoveryBlob({ ciphertext });
    const status: RecoveryBackupStatus = {
      kind: 'completed',
      uploadedAt: Date.now(),
      bytes: ciphertext.length,
    };
    await db.settings_kv.put({
      key: RECOVERY_BACKUP_KV_KEY,
      value: status,
      updatedAt: Date.now(),
    });
    setBackupStatus(status);
    log.info({ event: 'settings.recovery.rotatedV3', bytes: ciphertext.length });
    return mnemonic;
  }, []);

  const handleCopyRecoveryKey = useCallback(async (text: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return;
    }
    throw new Error('Clipboard not available — copy manually.');
  }, []);

  const handleDownloadRecoveryKey = useCallback(async (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }, []);

  // Mirrors the avatar flow from the old web AccountPane: presign,
  // PUT to S3, then PUT /v1/profiles with the new blob key. The
  // shared `AccountPane` doesn't carry the bytes through
  // `onAvatarPicked` (RN has no `File`), so on web we wire the
  // hidden `<input type=file>` here at the host and call the
  // uploader directly when the file lands.
  const handleAvatarFile = useCallback(async (file: File): Promise<void> => {
    // Send the file's ACTUAL content type (png/webp/jpeg) rather than forcing
    // jpeg — S3 stored the wrong MIME otherwise, so a PNG came back undisplayable.
    const contentType: AvatarContentType =
      file.type === 'image/webp'
        ? 'image/webp'
        : file.type === 'image/png'
          ? 'image/png'
          : 'image/jpeg';
    setAvatarUploading(true);
    try {
      const upload = await requestAvatarUploadUrl(contentType);
      await uploadAvatarBytes(upload, file);
      // Avatar bind stays on the legacy `PUT /v1/profiles` until the
      // canonical media-id pipeline ships; then this should switch
      // to `updateProfile({ avatarMediaId: ... })`. Display-name
      // updates already ride PATCH /v1/me/profile above.
      await legacyUpdateProfileAvatar(upload.blobKey);
      // Resolve the new media id → download URL and publish to ALL surfaces
      // (this modal, sidebar chip, chat self-avatar) via the self-profile store,
      // so the new avatar shows instantly without a reload.
      const url = await fetchMediaDownloadUrl(upload.blobKey).catch(() => undefined);
      if (url !== undefined) setAvatarUrl(url);
      useSelfProfile.getState().setProfile({
        avatarMediaId: upload.blobKey,
        ...(url !== undefined ? { avatarUrl: url } : {}),
      });
      // Realtime multi-device: nudge our OTHER devices to re-pull the profile.
      void sendSelfProfileChanged(
        (useSessionStore.getState().status as { client?: unknown }).client ?? null,
      );
      log.info({ event: 'settings.profile.avatar.saved' });
    } catch (err) {
      log.warn({
        event: 'settings.profile.avatar.failed',
        error: (err as Error).message,
      });
    } finally {
      setAvatarUploading(false);
    }
  }, []);

  // Host-only because the shared `AccountPane` is RN-based and RN
  // has no `<input type=file>` analogue. Click forwarding via the
  // `onAvatarPress` extension on `SettingsModal`.
  const handleAvatarPress = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Satisfies the v2 prop surface even though on web all the actual
  // work happens via `handleAvatarPress` + the file input's onChange.
  // Required so the "Change" button renders.
  const handleAvatarPicked = useCallback(async (): Promise<void> => {
    // no-op: the real flow runs inside the <input>'s onChange below.
  }, []);

  if (!visible) return null;

  // Redeeming a pairing code is the *new-device* half of linking. It
  // requires the HPKE ephemeral private key minted during the pre-auth
  // "Link this device" flow (`LinkDevicePairingHost`). A normal web
  // sign-up never writes that key — hide the redeem affordance so users
  // don't scan the fallback ticket QR back into the browser they just
  // paired from (the existing device only scans the *handshake* QR).
  const linkingPrivAvailable =
    typeof sessionStorage !== 'undefined' &&
    sessionStorage.getItem('ping.linkingPriv.b64u') != null &&
    sessionStorage.getItem('ping.linkingPriv.b64u')!.length > 0;

  // Prefer the server-canonical display name. The local `identity.displayName`
  // is empty on a freshly LINKED device (link-verify stores `displayName: ''`
  // and relies on a profile fetch to fill it — see LinkDevicePairingHost), so
  // binding the Account pane to it alone left the name blank after linking.
  // `serverProfile` (GET /v1/me/profile, loaded on open) is authoritative.
  const displayName = (serverProfile?.displayName || identity?.displayName) ?? '';
  const deviceLabel =
    typeof navigator !== 'undefined' && navigator.platform ? navigator.platform : 'This browser';

  return (
    <>
      <SettingsModal
        visible={visible}
        onClose={() => router.closeModal()}
        accountDisplayName={displayName}
        accountEmail={serverProfile?.contactEmail ?? ''}
        accountPhone={serverProfile?.contactPhone ?? ''}
        {...(avatarUrl !== undefined ? { accountAvatarUrl: avatarUrl } : {})}
        accountAvatarUploading={avatarUploading}
        {...(serverProfile?.bio != null ? { accountBio: serverProfile.bio } : {})}
        {...(serverProfile?.jobTitle != null ? { accountJobTitle: serverProfile.jobTitle } : {})}
        {...(serverProfile?.companyName != null
          ? { accountCompany: serverProfile.companyName }
          : {})}
        {...(serverProfile !== null ? { accountDiscoverable: serverProfile.discoverable } : {})}
        onToggleDiscoverable={handleToggleDiscoverable}
        accountKycStatus={kycStatus}
        onKycStart={handleKycStart}
        onReplayHomeTour={replayHomeTour}
        onReplaySettingsTour={replaySettingsTour}
        onOpenPaneGuide={(pane) => usePaneGuideStore.getState().open(pane)}
        currentDeviceLabel={deviceLabel}
        currentDeviceSubtitle="Web session"
        onDisplayNameSaved={handleDisplayNameSaved}
        onSignOutAllDevices={handleSignOutAllDevices}
        onSignOutDevices={handleSignOutDevices}
        onSignOutCurrentDevice={handleSignOutCurrentDevice}
        onEraseThisDevice={handleEraseThisDevice}
        onBuildLinkingTicket={handleBuildLinkingTicket}
        {...(linkingPrivAvailable ? { onAcceptLinkingTicket: handleAcceptLinkingTicket } : {})}
        onWatchForLinkPaired={handleWatchForLinkPaired}
        onUploadRecoveryBlob={handleUploadRecoveryBlob}
        onCopyRecoveryKey={handleCopyRecoveryKey}
        onDownloadRecoveryKey={handleDownloadRecoveryKey}
        onRevealRecoveryMnemonic={handleRevealRecoveryMnemonic}
        onConfirmRecoveryMnemonicSaved={handleConfirmRecoveryMnemonicSaved}
        onRotateRecoveryKey={handleRotateRecoveryKey}
        {...(backupStatus !== undefined ? { recoveryBackupStatus: backupStatus } : {})}
        renderQrCode={(value, size) => <QrRenderer value={value} size={size} />}
        onScanRequest={scanQrCode}
        onUpdateProfile={handleUpdateProfile}
        loadDevices={handleLoadDevices}
        loadSessions={handleLoadSessions}
        onAvatarPicked={handleAvatarPicked}
        onAvatarPress={handleAvatarPress}
        theme={preferences.theme}
        onThemeChange={handleThemeChange}
        accent={preferences.accentId}
        onAccentChange={handleAccentChange}
        background={preferences.chatBackground}
        onBackgroundChange={handleBackgroundChange}
        fontScale={preferences.fontScale}
        onFontScaleChange={handleFontScaleChange}
        notifications={notificationsSettings}
        preferences={preferencesSettings}
        storage={{
          usage: storageUsage,
          onRefresh: refreshStorage,
          onClearMediaCache: handleClearMediaCache,
          clearing: clearingStorage,
        }}
        onSetStatusPress={() => router.openModal('status')}
        {...(serverProfile?.status?.emoji != null
          ? { currentStatusEmoji: serverProfile.status.emoji }
          : {})}
        {...(serverProfile?.status?.text != null
          ? { currentStatusText: serverProfile.status.text }
          : {})}
      />
      {/*
				Web-only hidden file input. Lives at the host level so the
				shared (RN-based) AccountPane stays platform-agnostic. The
				`onAvatarPress` shim above wires the "Change" Pressable to
				this input via the ref.
			*/}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleAvatarFile(file);
          e.target.value = '';
        }}
      />
      {/* Embedded Persona KYC flow — opens in-page (no new tab/window). */}
      {kycInquiryId !== null && (
        <PersonaKycModal
          inquiryId={kycInquiryId}
          onComplete={(personaStatus) => {
            // The flow finished → close and reflect Persona's reported
            // outcome immediately (Verified / Declined / Under review). No
            // polling: the inquiry is terminal for the user, and the server
            // webhook reconciles the canonical value on the next open.
            setKycInquiryId(null);
            setKycStatus(mapPersonaInquiryStatus(personaStatus));
          }}
          onCancel={() => {
            setKycInquiryId(null);
            void refreshKycStatus();
          }}
          onError={(message) => {
            // Keep the modal OPEN on error so the user isn't left with a
            // silent "nothing happened" — the modal chrome (and Persona's
            // own error UI, if it loaded) stays visible. Most common cause:
            // the withpersona iframe is blocked by CSP (restart the dev
            // server after the vite.config CSP change) or the inquiry needs
            // a resume session token.
            log.warn({ event: 'kyc.embedded.error', message });
          }}
          onClose={() => setKycInquiryId(null)}
        />
      )}
    </>
  );
}
